import { effectScope, watch } from 'vue';
import {
  isAppOffline,
  isConnectivityError,
  isOfflineModeEnabled,
  isSimulatingOffline,
  markReachable,
  markUnreachable,
  shouldEnterOfflineMode,
  useOfflineDetection,
} from '../composables/useOfflineDetection';
import toastService from '../design-system/TwToast/toastService';

/**
 * Global offline awareness for the single app axios instance (TRIP-2666).
 *
 * Installed once in app.js, right after wrapAxiosWithCache(axios), on the RAW
 * axios instance — vue-axios shortcut methods (this.axios.get/post/…) are
 * bound to the raw instance, so raw-instance interceptors observe 100% of
 * component traffic. SWR cache hits (fromCache: true) are synthesized inside
 * useQueryWithCache's thenable and never traverse interceptors, so a cache hit
 * can never be mistaken for proof of connectivity.
 *
 * Three responsibilities:
 *   1. WRITE-GATE (request): while offline (flag on), mutations are rejected
 *      locally with an offline-blocked error instead of hitting a dead
 *      network. POST-based *reads* opt out per request with
 *      `config.offlineReadable = true` (mirrors the `cache:` idiom) — those
 *      requests double as recovery probes for screens that poll.
 *   2. AUTO-REACHABILITY (response): any successful same-origin response marks
 *      the server reachable; a connectivity error (no response / 502/503/504,
 *      never ERR_CANCELED) marks it unreachable. No per-component wiring.
 *   3. RECOVERY PROBE (online event): regaining network fires one no-auth
 *      GET /health-check; its success flows through (2) and clears the offline
 *      state app-wide, even on screens that never poll.
 *
 * Interceptor ordering (axios: request = LIFO, response = FIFO). At runtime:
 *   request:  App.vue pending-counter (registered last) → THIS GATE → SWR
 *             cache-key bookkeeping → dispatch
 *   response: SWR cache-write → THIS (reachability) → App.vue counter/redirect
 * A gate rejection therefore happens AFTER the pending counter increments and
 * lands in App.vue's response rejected-handler, which decrements it — the
 * counter stays balanced. If you add interceptors in app.js, keep this gate
 * registered after wrapAxiosWithCache so the SWR layer stays closest to
 * dispatch.
 */

const SAFE_METHODS = ['get', 'head', 'options'];

// No-auth probe endpoint (HealthCheckController). Not fos_js_routes-exposed,
// so the path is hardcoded here. Must NEVER be served from any cache (the
// service worker deliberately does not cache XHRs) — a cached 200 would
// falsely mark the server reachable.
const PROBE_PATH = '/health-check';

let installed = false;
let probeInFlight = false;
let onlineHandler = null;
let rejectionHandler = null;
let lastOfflineToastAt = 0;
let installedAxios = null;

// Background reconnect poll (TRIP-2666). The browser `online` event covers a
// network drop→restore, but a SERVER outage (network up, 502/503) fires no such
// event — so while offline we also probe on a timer with backoff: first after
// POLL_BASE_MS, doubling up to POLL_MAX_MS, until we're back. Stops the instant
// we reconnect (or the flag turns off). A test simulation pins us offline, so we
// skip the (futile) probe there but keep the loop cheap.
const POLL_BASE_MS = 30000;
const POLL_MAX_MS = 60000;
let pollTimer = null;
let pollDelay = POLL_BASE_MS;
let pollScope = null;

/**
 * Manual reachability probe — the "Try now" action and the background poll's
 * tick. Fires the same no-auth probe the `online` event uses; on success the
 * response interceptor calls markReachable() and the whole app returns to live
 * REACTIVELY (no page reload, SPA state preserved). Resolves regardless (TRIP-2666).
 */
export function retryReachability() {
  if (!installedAxios || probeInFlight) return Promise.resolve();
  probeInFlight = true;
  return installedAxios
    .get(PROBE_PATH)
    .catch(() => {
      /* still down — response interceptor kept us offline */
    })
    .finally(() => {
      probeInFlight = false;
    });
}

function stopReconnectPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  pollDelay = POLL_BASE_MS;
}

function scheduleReconnectPoll() {
  if (pollTimer) return; // already polling
  const tick = () => {
    pollTimer = null;
    if (!isAppOffline.value) {
      stopReconnectPoll();
      return;
    }
    if (!isSimulatingOffline()) retryReachability();
    pollDelay = Math.min(pollDelay * 2, POLL_MAX_MS);
    pollTimer = setTimeout(tick, pollDelay);
    if (pollTimer && typeof pollTimer.unref === 'function') pollTimer.unref();
  };
  pollTimer = setTimeout(tick, pollDelay);
  if (pollTimer && typeof pollTimer.unref === 'function') pollTimer.unref();
}

function isSameOrigin(url) {
  if (!url || !/^https?:\/\//i.test(url)) return true; // relative → same origin
  if (typeof window === 'undefined') return false;
  return url.startsWith(`${window.location.origin}/`) || url === window.location.origin;
}

function isGatedWrite(config) {
  const method = (config.method || 'get').toLowerCase();
  if (SAFE_METHODS.includes(method)) return false;
  if (config.offlineReadable) return false;
  return isSameOrigin(config.url);
}

export function createOfflineBlockedError(config) {
  const error = new Error(
    "You're offline — this change can't be saved right now. Editing will be available again once you reconnect.",
  );
  error.isOfflineBlocked = true;
  error.code = 'ERR_OFFLINE_BLOCKED';
  // `config` set + no `response` makes isConnectivityError(error) === true on
  // purpose: screens that already branch on shouldEnterOfflineMode treat a
  // blocked write exactly like an outage, with no code changes.
  error.config = config;
  return error;
}

export function isOfflineBlockedError(error) {
  return !!(error && error.isOfflineBlocked);
}

// A webpack dynamic-import failure (a JS/CSS chunk not in the SW cache and
// unreachable offline). webpack sets error.name = 'ChunkLoadError'; we also
// match the message defensively across browsers (TRIP-2666).
function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk .* failed/i.test(error.message || '');
}

export function installOfflineInterceptors(axiosInstance) {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  installedAxios = axiosInstance;

  const { isOffline } = useOfflineDetection();

  axiosInstance.interceptors.request.use((config) => {
    if (!isOfflineModeEnabled() || !isOffline.value || !isGatedWrite(config)) {
      return config;
    }
    return Promise.reject(createOfflineBlockedError(config));
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      if (isOfflineModeEnabled() && isSameOrigin(response.config?.url)) {
        markReachable();
      }
      return response;
    },
    (error) => {
      if (!isOfflineBlockedError(error) && isSameOrigin(error?.config?.url) && shouldEnterOfflineMode(error)) {
        markUnreachable();
      }
      return Promise.reject(error);
    },
  );

  onlineHandler = () => {
    if (!isOfflineModeEnabled() || !isOffline.value || probeInFlight) return;
    probeInFlight = true;
    axiosInstance
      .get(PROBE_PATH)
      .catch(() => {
        /* still down — the response interceptor already kept us offline */
      })
      .finally(() => {
        probeInFlight = false;
      });
  };
  window.addEventListener('online', onlineHandler);

  // GLOBAL SAFETY NET (TRIP-2666): screens that don't yet handle offline can
  // reject an offline-blocked write OR a connectivity error that nothing
  // catches — which would otherwise surface as an uncaught runtime error (dev
  // overlay) or Bugsnag noise. Swallow those specific rejections and show a
  // single throttled toast instead, so the whole app degrades gracefully even
  // before a screen is individually adopted. Any other rejection is untouched.
  rejectionHandler = (event) => {
    const reason = event && event.reason;
    const isOffline =
      isOfflineBlockedError(reason) ||
      (isOfflineModeEnabled() && isConnectivityError(reason)) ||
      // A build chunk that isn't cached can't be fetched offline — swallow the
      // ChunkLoadError (no crash overlay) and toast instead. Only while actually
      // offline, so a stale-deploy chunk error online still surfaces normally.
      (isAppOffline.value && isChunkLoadError(reason));
    if (!isOffline) return;
    event.preventDefault();
    const now = Date.now();
    if (now - lastOfflineToastAt > 4000) {
      lastOfflineToastAt = now;
      toastService.add({
        title: "You're offline",
        message: "Some data and actions need a connection. We'll reconnect automatically.",
        type: 'warning',
      });
    }
  };
  window.addEventListener('unhandledrejection', rejectionHandler);

  // Run/stop the background reconnect poll in lockstep with offline state.
  // effectScope keeps the watcher valid outside any component setup().
  pollScope = effectScope(true);
  pollScope.run(() => {
    watch(
      isAppOffline,
      (off) => {
        if (off) scheduleReconnectPoll();
        else stopReconnectPoll();
      },
      { immediate: true },
    );
  });
}

/** Test-only: allows re-installing on a fresh axios instance between tests. */
export function resetOfflineInterceptorsForTests() {
  if (typeof window !== 'undefined') {
    if (onlineHandler) window.removeEventListener('online', onlineHandler);
    if (rejectionHandler) window.removeEventListener('unhandledrejection', rejectionHandler);
  }
  stopReconnectPoll();
  if (pollScope) {
    pollScope.stop();
    pollScope = null;
  }
  onlineHandler = null;
  rejectionHandler = null;
  installedAxios = null;
  installed = false;
  probeInFlight = false;
  lastOfflineToastAt = 0;
}
